import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * PlanningCalendar — month grid that visualises daily load.
 *
 * Props:
 *  - data: [{ date: "YYYY-MM-DD", ...metrics }]
 *  - metric: string key inside each row to display (e.g. "total" or "adults")
 *  - accent: tailwind class prefix for cell intensity (e.g. "orange" | "navy")
 *  - onSelectDate?: (dateIso) => void
 *  - label: what the metric represents (used in cell tooltip and header)
 *
 * The calendar starts on Monday (Turkish/European convention). Days without
 * data are rendered dimmed. Days from the previous/next month appear muted.
 */
const DAY_LABELS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MONTH_LABELS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function PlanningCalendar({ data = [], metric = "total", accent = "orange", onSelectDate, label = "Toplam" }) {
  const today = new Date();
  const [cursor, setCursor] = useState(startOfMonth(today));
  const [selected, setSelected] = useState(null);

  // Build a Map(dateIso -> row) for O(1) look-ups.
  const dataMap = useMemo(() => Object.fromEntries((data || []).map((r) => [r.date, r])), [data]);

  const maxVal = useMemo(() => {
    let m = 0;
    for (const r of data) m = Math.max(m, Number(r[metric] || 0));
    return m || 1;
  }, [data, metric]);

  // Compose an array of 6 x 7 = 42 cells starting from the Monday of the
  // week that contains the first day of the current month.
  const cells = useMemo(() => {
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    // JS: 0 = Sunday. We want Monday-first, so offset accordingly.
    const dowMondayFirst = (monthStart.getDay() + 6) % 7;
    const start = new Date(monthStart);
    start.setDate(monthStart.getDate() - dowMondayFirst);
    const out = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const iso = isoDate(d);
      const row = dataMap[iso];
      out.push({
        date: d,
        iso,
        inMonth: d.getMonth() === cursor.getMonth(),
        row,
        value: row ? Number(row[metric] || 0) : 0,
      });
    }
    return out;
  }, [cursor, dataMap, metric]);

  const intensityCls = (v) => {
    if (!v) return "bg-slate-50";
    const ratio = v / maxVal;
    if (accent === "navy") {
      if (ratio > 0.75) return "bg-brand-navy text-white";
      if (ratio > 0.5) return "bg-brand-navy/80 text-white";
      if (ratio > 0.25) return "bg-brand-navy/50 text-white";
      if (ratio > 0.1) return "bg-brand-navy/30 text-brand-navy";
      return "bg-brand-navy/15 text-brand-navy";
    }
    // default orange (warm/food-y)
    if (ratio > 0.75) return "bg-brand-orange text-white";
    if (ratio > 0.5) return "bg-brand-orange/80 text-white";
    if (ratio > 0.25) return "bg-brand-orange/50 text-white";
    if (ratio > 0.1) return "bg-brand-orange/25 text-brand-orange-dark";
    return "bg-brand-orange/10 text-brand-orange-dark";
  };

  const handleClick = (c) => {
    setSelected(c.iso);
    if (onSelectDate) onSelectDate(c.iso);
  };

  return (
    <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="planning-calendar">
      <div className="flex items-center justify-between gap-2 mb-4">
        <button
          type="button"
          onClick={() => setCursor(addMonths(cursor, -1))}
          className="w-9 h-9 rounded-full border border-brand-navy/20 flex items-center justify-center hover:bg-brand-navy/5"
          data-testid="calendar-prev"
          aria-label="Önceki ay"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-center">
          <div className="font-serif text-xl text-brand-navy" data-testid="calendar-title">
            {MONTH_LABELS[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
        </div>
        <button
          type="button"
          onClick={() => setCursor(addMonths(cursor, 1))}
          className="w-9 h-9 rounded-full border border-brand-navy/20 flex items-center justify-center hover:bg-brand-navy/5"
          data-testid="calendar-next"
          aria-label="Sonraki ay"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest text-slate-400 mb-1">
        {DAY_LABELS.map((d) => (
          <div key={d} className="text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c) => {
          const isToday = isSameDay(c.date, today);
          const isSelected = selected === c.iso;
          return (
            <button
              key={c.iso}
              type="button"
              onClick={() => handleClick(c)}
              disabled={!c.inMonth}
              className={`aspect-square rounded-lg text-xs sm:text-sm font-medium flex flex-col items-center justify-center relative transition ${
                c.inMonth ? intensityCls(c.value) : "bg-transparent text-slate-300"
              } ${isSelected ? "ring-2 ring-brand-navy" : ""} ${isToday ? "ring-2 ring-brand-gold" : ""}`}
              data-testid={`calendar-cell-${c.iso}`}
              title={`${c.iso} · ${label}: ${c.value}`}
            >
              <span className="text-[11px] opacity-80">{c.date.getDate()}</span>
              {c.inMonth && c.value > 0 && (
                <span className="text-base font-serif leading-none mt-0.5">{c.value}</span>
              )}
              {isToday && <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-brand-gold" />}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-3 text-[10px] text-slate-500">
        <div className="flex items-center gap-1"><span className={`inline-block w-4 h-4 rounded ${intensityCls(0.15 * maxVal)}`} /> Düşük</div>
        <div className="flex items-center gap-1"><span className={`inline-block w-4 h-4 rounded ${intensityCls(0.5 * maxVal)}`} /> Orta</div>
        <div className="flex items-center gap-1"><span className={`inline-block w-4 h-4 rounded ${intensityCls(maxVal)}`} /> Yüksek ({maxVal})</div>
      </div>
    </div>
  );
}
